/**
 * Customer page → Invoices tab: the received-payment strip (#1916).
 *
 *  - "Record a payment" — opens the one-check screen for this customer;
 *  - credit kept on the account (pick 5), each with its reason;
 *  - "Same payer" accounts (pick 7): set once, by an admin, no merging;
 *  - "Email one receipt per payment" (pick 8): off until someone turns it on.
 */
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Link2, Unlink, Wallet } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { AsyncPicker } from "@/components/async-picker";
import { centsOf, money } from "@/pages/money/payments/payment-copy";
import { recordPaymentHref, useRecordMany } from "@/pages/money/payments/use-record-many";

export function CustomerPaymentStrip({
    customerId,
    selectedInvoiceIds,
}: {
    customerId: string;
    selectedInvoiceIds: string[];
}) {
    const on = useRecordMany();
    const qc = useQueryClient();
    const navigate = useNavigate();
    const [linking, setLinking] = useState(false);
    const credits = useQuery({
        queryKey: ["customer-credits", customerId],
        queryFn: () =>
            api.get<{
                credits: Array<{
                    id: string;
                    remaining: number;
                    reason: string;
                    source_received_payment_id: string | null;
                }>;
                total: number;
            }>(`/api/customers/${customerId}/credits`),
        enabled: on,
    });
    const payer = useQuery({
        queryKey: ["same-payer", customerId],
        queryFn: () =>
            api.get<{ accounts: Array<{ id: string; company_name: string | null }>; can_edit: boolean }>(
                `/api/customers/${customerId}/same-payer`,
            ),
        enabled: on,
    });
    const receipt = useQuery({
        queryKey: ["payment-workspace-receipt", customerId],
        queryFn: () => api.get<{ receipt_on: boolean }>(`/api/customers/${customerId}/payment-workspace`),
        enabled: on,
    });
    const link = useMutation({
        mutationFn: (otherCustomerId: string) =>
            api.post(`/api/customers/${customerId}/same-payer`, { otherCustomerId }),
        onSuccess: () => {
            toast.success("Linked. Their open invoices now show together on Record a payment.");
            setLinking(false);
            qc.invalidateQueries({ queryKey: ["same-payer"] });
        },
        onError: (e: any) => toast.error(e?.body?.message ?? e?.message ?? "Couldn't link."),
    });
    const unlink = useMutation({
        mutationFn: () => api.delete(`/api/customers/${customerId}/same-payer`),
        onSuccess: () => {
            toast.success("Taken out of the same-payer group.");
            qc.invalidateQueries({ queryKey: ["same-payer"] });
        },
        onError: (e: any) => toast.error(e?.body?.message ?? e?.message ?? "Couldn't unlink."),
    });
    const setReceipt = useMutation({
        mutationFn: (v: boolean) => api.put(`/api/customers/${customerId}/grouped-receipt`, { on: v }),
        onSuccess: () => qc.invalidateQueries({ queryKey: ["payment-workspace-receipt", customerId] }),
        onError: (e: any) => toast.error(e?.body?.message ?? e?.message ?? "Couldn't save."),
    });

    if (!on) return null;
    const others = payer.data?.accounts ?? [];
    const creditTotal = centsOf(credits.data?.total ?? 0);
    const receiptOn = Boolean(receipt.data?.receipt_on);

    return (
        <div className="mb-4 rounded-lg border bg-card p-3 space-y-3" data-testid="customer-payment-strip">
            <div className="flex flex-wrap items-center gap-3">
                <Wallet className="h-5 w-5 text-primary" />
                <p className="flex-1 min-w-[14rem] text-sm font-semibold">One check for many of these invoices?</p>
                <Button onClick={() => navigate(recordPaymentHref(customerId, selectedInvoiceIds))}>
                    Record a payment{selectedInvoiceIds.length ? ` (${selectedInvoiceIds.length} ticked)` : ""}
                </Button>
            </div>
            <div className="grid gap-3 text-sm md:grid-cols-3">
                <div className="rounded-lg bg-secondary p-2.5">
                    <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        Credit on the account
                    </p>
                    <p className="font-mono text-lg font-semibold tabular-nums text-primary">{money(creditTotal)}</p>
                    {(credits.data?.credits ?? []).slice(0, 3).map((c) => (
                        <p key={c.id} className="text-xs text-muted-foreground">
                            {money(centsOf(c.remaining))} · {c.reason}
                            {c.source_received_payment_id && (
                                <>
                                    {" "}
                                    ·{" "}
                                    <Link to={`/money/payments/${c.source_received_payment_id}`} className="underline">
                                        from this payment
                                    </Link>
                                </>
                            )}
                        </p>
                    ))}
                </div>
                <div className="rounded-lg bg-secondary p-2.5">
                    <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Same payer</p>
                    {others.length === 0 ? (
                        <p className="text-xs text-muted-foreground">Not linked to any other account.</p>
                    ) : (
                        others.map((a) => (
                            <Link
                                key={a.id}
                                to={`/customers/${a.id}`}
                                className="block text-sm font-semibold hover:underline"
                            >
                                {a.company_name}
                            </Link>
                        ))
                    )}
                    {payer.data?.can_edit && (
                        <div className="mt-1.5 space-y-1.5">
                            {linking ? (
                                <AsyncPicker
                                    value={null}
                                    onChange={(id) => id && link.mutate(id)}
                                    placeholder="Search the other account…"
                                    queryKeyBase="same-payer-pick"
                                    buildSearchPath={(q) => `/api/customers?q=${encodeURIComponent(q)}&limit=20`}
                                    mapResults={(data) =>
                                        (
                                            (data as { customers?: { id: string; company_name: string }[] })
                                                .customers ?? []
                                        )
                                            .filter((c) => c.id !== customerId)
                                            .map((c) => ({ id: c.id, label: c.company_name }))
                                    }
                                    buildResolvePath={(id) => `/api/customers/${id}`}
                                    mapResolvedLabel={(data) =>
                                        (data as { customer?: { company_name?: string } }).customer?.company_name ??
                                        "Customer"
                                    }
                                />
                            ) : (
                                <Button size="sm" variant="outline" onClick={() => setLinking(true)}>
                                    <Link2 className="h-3.5 w-3.5" /> Link an account that pays with them
                                </Button>
                            )}
                            {others.length > 0 && (
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    disabled={unlink.isPending}
                                    onClick={() => unlink.mutate()}
                                >
                                    <Unlink className="h-3.5 w-3.5" /> Take this one out
                                </Button>
                            )}
                        </div>
                    )}
                </div>
                <div className="rounded-lg bg-secondary p-2.5">
                    <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Receipt email</p>
                    <div className="mt-1 flex gap-1.5" role="radiogroup" aria-label="Email one receipt per payment">
                        <Button
                            size="sm"
                            variant={receiptOn ? "default" : "outline"}
                            onClick={() => !receiptOn && setReceipt.mutate(true)}
                        >
                            On
                        </Button>
                        <Button
                            size="sm"
                            variant={!receiptOn ? "default" : "outline"}
                            onClick={() => receiptOn && setReceipt.mutate(false)}
                        >
                            Off
                        </Button>
                    </div>
                    <p className="mt-1 text-xs text-muted-foreground">
                        When on, they get ONE receipt per payment listing every invoice (only while receipt emails are
                        on for the company).
                    </p>
                </div>
            </div>
        </div>
    );
}
